import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  convertAmountWithRate,
  fetchExchangeRate,
  getCurrencySymbol,
  getRestaurantCurrencyCode,
  getRestaurantSecondaryCurrencyCode,
} from "../../../utils/currency";
import CurrencyFlag from "../AppsMenu/AppProfile/CurrencyFlag";
import "./DualPriceFields.css";

export function AutoConvertButton({
  amount,
  fromCode,
  toCode,
  onConverted,
  disabled = false,
  compact = false,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState("");
  const [error, setError] = useState("");

  const convert = async () => {
    const source = Number(String(amount ?? "").replace(",", "."));
    if (!Number.isFinite(source) || String(amount ?? "").trim() === "") {
      setError(t("autoConvertNeedPrice"));
      setMeta("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { rate } = await fetchExchangeRate(fromCode, toCode);
      const converted = convertAmountWithRate(source, rate);
      onConverted?.(converted);
      setMeta(
        t("autoConvertRate", {
          from: fromCode,
          to: toCode,
          rate: Number(rate).toFixed(4),
        })
      );
    } catch {
      setError(t("autoConvertFailed"));
      setMeta("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`auto-convert ${compact ? "auto-convert--compact" : ""}`}>
      <button
        type="button"
        className={`auto-convert__btn${compact ? " auto-convert__btn--stacked" : ""}`}
        onClick={convert}
        disabled={disabled || loading || !fromCode || !toCode}
        title={error || meta || t("autoConvert")}
      >
        {compact ? <i className="fas fa-exchange-alt" aria-hidden="true" /> : null}
        {loading ? t("autoConvertLoading") : t("autoConvert")}
      </button>
      {compact ? null : meta ? (
        <small className="auto-convert__meta">{meta}</small>
      ) : null}
      {compact ? null : error ? (
        <small className="auto-convert__error">{error}</small>
      ) : null}
    </div>
  );
}

export default function DualPriceFields({
  price,
  secondaryPrice,
  onPriceChange,
  onSecondaryPriceChange,
  disabled = false,
}) {
  const { t } = useTranslation();
  const mainCode = getRestaurantCurrencyCode();
  const secondaryCode = getRestaurantSecondaryCurrencyCode();
  const disabledStyle = disabled
    ? { backgroundColor: "#f0f0f0", cursor: "not-allowed" }
    : { backgroundColor: "white", cursor: "text" };

  return (
    <>
      <h4 style={{ marginTop: 10 }}>
        {t("price")} (
        <CurrencyFlag code={mainCode} className="currency-flag--inline" /> {mainCode}{" "}
        {getCurrencySymbol(mainCode)})
        <span className="badge bg-primary ms-2">{t("currencyMainBadge")}</span>
      </h4>
      <input
        type="number"
        className="form-control"
        style={disabledStyle}
        disabled={disabled}
        value={price}
        step="0.01"
        onChange={(e) => onPriceChange(e.target.value.replace(",", "."))}
      />
      {secondaryCode ? (
        <>
          <div className="dual-price-secondary-head">
            <h4>
              {t("secondaryPrice")} (
              <CurrencyFlag code={secondaryCode} className="currency-flag--inline" />{" "}
              {secondaryCode} {getCurrencySymbol(secondaryCode)})
              <span className="badge bg-light text-dark border ms-2">{t("optional")}</span>
            </h4>
            <AutoConvertButton
              amount={price}
              fromCode={mainCode}
              toCode={secondaryCode}
              disabled={disabled}
              onConverted={onSecondaryPriceChange}
            />
          </div>
          <input
            type="number"
            className="form-control"
            style={disabledStyle}
            disabled={disabled}
            value={secondaryPrice ?? ""}
            step="0.01"
            placeholder={t("secondaryPricePlaceholder")}
            onChange={(e) =>
              onSecondaryPriceChange(e.target.value.replace(",", "."))
            }
          />
        </>
      ) : null}
    </>
  );
}

export function ExtraSizeDualPriceInputs({
  size,
  onFinalPriceChange,
  onSecondaryFinalPriceChange,
}) {
  const { t } = useTranslation();
  const mainCode = getRestaurantCurrencyCode();
  const secondaryCode = getRestaurantSecondaryCurrencyCode();

  return (
    <>
      <input
        type="number"
        className="form-control"
        placeholder={`${t("price")} (${mainCode})`}
        value={size.finalPrice}
        onChange={(e) => onFinalPriceChange(e.target.value)}
      />
      {secondaryCode ? (
        <>
          <input
            type="number"
            className="form-control ms-2"
            placeholder={`${t("secondaryPrice")} (${secondaryCode})`}
            value={size.secondaryFinalPrice ?? ""}
            onChange={(e) => onSecondaryFinalPriceChange(e.target.value)}
          />
          <AutoConvertButton
            compact
            amount={size.finalPrice}
            fromCode={mainCode}
            toCode={secondaryCode}
            onConverted={onSecondaryFinalPriceChange}
          />
        </>
      ) : null}
    </>
  );
}
